import { type Options } from '@glitz/core';
import transformers from '@glitz/transformers';
import { type Property } from 'csstype';
import { mediaQueryOrder } from './media';
import { GlitzClient, GlitzServer } from '@glitz/core';

// This file must be able to be used on both server- (Node) and client side bundles

interface Color {
  color: string;
  hover: string;
  text: string;
  hoverText?: string;
  border?: string;
  hoverBorder?: string;
}

interface Style {
  color?: string;
  text?: string;
  fontSize?: number;
  fontWeight?: number;
  textTransform?: Property.TextTransform | NonNullable<Property.TextTransform | undefined>[];
  borderColor?: string;
  fontFamily?: string;
}

interface Spacing {
  xxxxl: number;
  xxxl: number;
  xxl: number;
  xl: number;
  l: number;
  m: number;
  s: number;
  xs: number;
  xxs: number;
}

interface Size {
  xxxl: number;
  xxl: number;
  xl: number;
  l: number;
  m: number;
  s: number;
  xs: number;
  xxs: number;
  xxxs: number;
}

interface Radius {
  primary: number | string;
  secondary: number | string;
}

interface FontFamily {
  primary: string;
  secondary: string;
  tertiary: string;
}
interface MobileFooter {
  backgroundColor: string;
  color?: string;
  borderColor: string;
}

interface Footer extends Style {
  heading?: Style;
  infoFooter?: Style;
  accordion?: MobileFooter;
}

interface QuickSearch {
  borderColor: string;
  backgroundColor: string;
  borderRadius: number;
  highlightText: string;
}

interface SearchSuggestions {
  borderRadius: number;
  position: number;
  borderColor: string;
  color: string;
  backgroundColor: string;
}

interface Heading {
  fontWeight?: number;
  textTransform?: Property.TextTransform | NonNullable<Property.TextTransform | undefined>[];
}

interface Price {
  current: { card: Style; page: Style };
  original: Style;
  vatText: Style;
  discount: Style;
}

interface ProductCard {
  title: Style;
  brand: Style;
  price: Style;
  wishlistIcon: Style;
}

interface Header {
  backgroundColor: string;
  fontSize: number;
  fontWeight: number;
  menuTextTransform: Property.TextTransform | NonNullable<Property.TextTransform | undefined>[];
  subCategoryFontSize: number;
  menuHeadingFontSize: number;
  menuHightlightColor: string;
  topMenu: Style;
}

interface MobileMenu {
  backgroundColor: string;
  accentColor: string;
  borderColor: string;
  fontSize?: number;
  fontWeight: number;
  tabs: Style;
}

interface Facets {
  flyoutHeader: Style;
  heading: Style;
  facet: Style;
  count: Style;
  borderColor: string;
  search: Style;
  range: RangeFacet;
  adornment: AdornmentFacet;
}

interface Flyout {
  headerColor: string;
  headerHeight: number;
  headerTextPosition: string;
  backgroundColor: string;
}

interface RangeFacet {
  sliderTrackColor: string;
  sliderMarkColor: string;
  sliderRailColor: string;
  markDiameter: number;
  railHeight: number;
}

interface AdornmentFacet {
  input: Style;
  affix: Style;
}

interface TopBanner {
  color: string;
  text: string;
  checkColor: string;
}

interface FontWeight {
  normal: number;
  semiBold?: number;
  bold: number;
  light?: number;
  primary: number;
  secondary: number;
}

interface Indicators {
  cart: { backgroundColor: string; color: string };
  favourites: { backgroundColor: string; color: string };
}

interface Button {
  textTransform?: Property.TextTransform | NonNullable<Property.TextTransform | undefined>[];
  fontWeight?: number;
}

interface Checkbox {
  color: string;
  borderColor: string;
  disabled: string;
  checkedBackgroundColor?: string;
  borderRadius?: number;
  uncheckedColor?: string;
  checkedColor?: string;
}

interface Breadcrumbs {
  fontSizeLargeViewport: number;
  fontSizeBase: number;
  segmentFontWeight: string | number;
  lastSegmentColor: string;
  color: string;
}

interface Bubble {
  backgroundColor: string;
  color: string;
}

interface Scrollbar {
  trackColor: string;
  thumbColor: string;
  radius: number;
}

interface Ratings {
  backgroundColor: string;
  color: string;
}

interface Stockstatus {
  inStock: string;
  lowInStock: string;
  outOfStock: string;
  unknown: string;
}

interface FocialPoint {
  imageBackgroundColor: string;
  iconBackgroundColor: string;
}

interface Checkout {
  backgroundColor: string;
  text: string;
}

interface MaterialSamples {
  titleFontWeight: number;
  descriptionFontWeight: number;
  stockStatusFontWeight: number;
}

declare module '@glitz/core' {
  interface Theme {
    id: string;
    primary: Color;
    secondary: Color;
    tertiary: Color;
    quaternary: Color;
    monochrome: Color;
    monochromeBrighter: Color;
    monochromeLight: string;
    creamWhite: string;
    positive: Color;
    negative: Color;
    transparent: Color;
    white: Color;
    dark: Color;
    disabled: Color;
    secondaryLight: string;
    secondaryBrighter: string;
    secondaryBrightest: string;
    darkGrey: string;
    actionButton: Color;
    filterButton: Color;
    linkButton: Color;
    wishlistButton: Color;
    sharedSpacing: Spacing;
    spacing: Spacing;
    sharedFontSize: Size;
    fontSize: Size;
    radius: Radius;
    lightBackground: string;
    flyout: Flyout;
    borderColor: string;
    borderStrongColor: string;
    borderHighlight: string;
    headerIndicators: Indicators;
    breadcrumbs: Breadcrumbs;
    checkbox: Checkbox;
    carouselButton: Color;
    bubble: Bubble;
    ratings?: Ratings;
    fontFamily?: FontFamily;
    myPagesMenu?: Style;
    myPagesLoyaltyPoints?: Style;
    myPagesInfoBox?: Style;
    myPagesProfilePage?: Style;
    productPageInfo?: Style;
    footer?: Footer;
    memberClubNewsletter?: Style;
    quickSearch: QuickSearch;
    searchSuggestions?: SearchSuggestions;
    notificationColor?: Style;
    myPagesButton?: Style;
    myPagesText?: Style;
    partialDeliveryTheme?: Style;
    price?: Price;
    productCard?: ProductCard;
    scrollbar?: Scrollbar;
    header?: Header;
    topBanner?: TopBanner;
    fontWeight?: FontWeight;
    heading?: Heading;
    button?: Button;
    stockStatus?: Stockstatus;
    newsBadge?: Style;
    thumbnailBorder: string;
    mobileMenu?: MobileMenu;
    checkout?: Checkout;
    facets?: Facets;
    outline?: string;
    imageBackgroundColor: string;
    focalPoint: FocialPoint;
    inputFocusOutlineColor: string;
    materialSamples?: MaterialSamples;
  }
}

export const glitzOptions: Options = {
  transformer: transformers(),
  mediaOrder(a, b) {
    const indexA = mediaQueryOrder.indexOf(a);
    const indexB = mediaQueryOrder.indexOf(b);
    return indexA - indexB;
  },
};

export let glitz: GlitzClient | GlitzServer;
function createGlitz() {
  if (__BROWSER__) {
    glitz = new GlitzClient(glitzOptions);
  } else {
    glitz = new GlitzServer(glitzOptions);
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', createGlitz);
  } else {
    createGlitz();
  }
} else {
  createGlitz();
}
